<template>
  <div class="entity-detail" v-show="ready">
    <div class="entity-detail-header">
      <div class="title">
        <h1>{{ name }}</h1>
        <span v-if="disambiguation">（{{ disambiguation }}）</span>
      </div>
      <div
        v-if="$slots['basic-info-actions']"
        class="entity-basic-info-actions"
      >
        <slot name="basic-info-actions" />
      </div>
    </div>

    <div
      class="entity-meta"
      v-if="editable || (alias && alias.length) || typeList?.length"
    >
      <div class="meta-field" v-if="editable || (alias && alias.length)">
        <h3 class="meta-label">别名</h3>
        <div class="meta-body">
          <template v-if="editable">
            <el-tag
              v-for="(aliasItem, aliasIndex) in alias"
              :key="`${aliasItem}-${aliasIndex}`"
              class="entity-alias-tag"
              closable
              :disable-transitions="true"
              @close="removeAlias(aliasIndex)"
            >
              {{ aliasItem }}
            </el-tag>
            <el-input
              v-model="aliasInput"
              class="alias-add-input"
              size="default"
              placeholder="输入别名，回车添加"
              @keyup.enter="addAliasFromInput"
              @blur="addAliasFromInput"
            />
          </template>
          <template v-else>
            <el-tag
              v-for="(aliasItem, aliasIndex) in alias"
              :key="aliasIndex"
              class="entity-alias-tag"
              :disable-transitions="true"
            >
              {{ aliasItem }}
            </el-tag>
          </template>
        </div>
      </div>

      <div
        class="meta-field meta-field--type"
        v-if="editable || typeList?.length"
      >
        <template v-if="editable">
          <h3 class="meta-label">实体类别</h3>
          <div class="meta-body">
            <span v-if="!categoryOptions.length" class="meta-empty-hint">
              暂无可用类别，请先在「实体类别管理」中创建
            </span>
            <el-tag
              v-for="c in selectedCategoryItems"
              :key="c.code"
              class="entity-type-tag entity-type-tag--editable"
              closable
              :disable-transitions="true"
              @close="removeTypeCode(c.code)"
            >
              {{ c.name }}
            </el-tag>
            <el-dropdown
              v-if="availableCategoryOptions.length"
              trigger="click"
              @command="addTypeCode"
            >
              <el-button
                class="meta-add-btn"
                circle
                size="small"
                type="primary"
                plain
              >
                <el-icon><Plus /></el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="c in availableCategoryOptions"
                    :key="c.code"
                    :command="c.code"
                  >
                    {{ c.name }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </template>
        <template v-else>
          <h3 class="meta-label">实体类别</h3>
          <div class="meta-body">
            <el-tag
              class="entity-type-tag"
              :disable-transitions="true"
              v-for="(typeItem, typeIndex) in typeList"
              :key="typeIndex"
            >
              {{ typeItem }}
            </el-tag>
          </div>
        </template>
      </div>
    </div>

    <div class="entity-description" v-if="editable || description">
      <h3 class="entity-description-title">描述</h3>
      <template v-if="editable">
        <el-input
          :model-value="description"
          type="textarea"
          :rows="4"
          placeholder="请输入描述"
          @update:model-value="emit('update:description', $event)"
        />
      </template>
      <template v-else>
        <div class="description-info" ref="descriptionInfoRef">
          <p class="kn-text-align-justify kn-ellipsis-multiple">
            <span class="description-text">{{ description }}</span>
          </p>
        </div>
        <div class="spread-informatin" v-if="ui.ifDescriptionCollapseVisible">
          <el-button size="small" @click="handleSpreadDescription">
            <i
              class="iconfont icon-doublearrow"
              :class="{ 'all-information': !ui.ifDescriptionCollapse }"
            ></i>
            <span>{{ ui.ifDescriptionCollapse ? "展开" : "收起" }}</span>
          </el-button>
        </div>
      </template>
    </div>

    <div class="entity-attribute" v-if="editable || attributesList?.length">
      <h3 class="entity-attribute-title">属性</h3>
      <template v-if="editable">
        <div
          v-for="(attributesItem, attributesIndex) in attributesList"
          :key="attributesIndex"
          class="attr-edit-row"
        >
          <el-input
            :model-value="attributesItem.name"
            placeholder="属性名"
            style="width: 35%"
            @update:model-value="
              updateAttributeField(attributesIndex, 'name', $event)
            "
          />
          <el-input
            :model-value="attributesItem.value"
            placeholder="属性值"
            style="width: 45%"
            @update:model-value="
              updateAttributeField(attributesIndex, 'value', $event)
            "
          />
          <el-button
            link
            type="danger"
            @click="removeEditableAttribute(attributesIndex)"
          >
            删除
          </el-button>
        </div>
        <el-button link type="primary" @click="addEditableAttribute">
          + 添加属性
        </el-button>
      </template>
      <template v-else>
        <el-row class="attribute-content">
          <el-col
            :span="12"
            v-for="(attributesItem, attributesIndex) in attributesList.slice(
              0,
              ui.defaultAttributeNum
            )"
            :key="attributesIndex"
          >
            <div class="attribute-item">
              <span class="attribute-name">{{
                formatAttributeDisplayName(attributesItem.name)
              }}</span>
              <span class="attribute-value">{{ attributesItem.value }}</span>
            </div>
          </el-col>
        </el-row>
        <div class="spread-informatin" v-if="ui.ifAttributionCollapseVisible">
          <el-button
            size="small"
            @click="handleSpreadAttribute(attributesList.length)"
          >
            <i
              class="iconfont icon-doublearrow"
              :class="{ 'all-information': !ui.ifAttributionCollapse }"
            ></i>
            <span>{{ ui.ifAttributionCollapse ? "展开" : "收起" }}</span>
          </el-button>
        </div>
      </template>
    </div>

    <div class="entity-relation" v-if="showRelationSection">
      <div class="entity-relation-header">
        <h3 class="entity-relation-title">关系</h3>
        <slot name="relation-actions" />
      </div>
      <p
        v-if="!relationDirectionGroups.length && adminMode"
        class="relation-empty-hint"
      >
        暂无关系，可点击「新建关系」添加
      </p>
      <div class="all-relation" v-if="relationDirectionGroups.length">
        <div
          class="relation-direction-card"
          :class="directionGroup.type"
          v-for="directionGroup in relationDirectionGroups"
          :key="directionGroup.type"
        >
          <div class="relation-direction-title">
            <span class="direction-icon">{{ directionGroup.icon }}</span>
            <span>{{ directionGroup.titlePrefix }}</span>
            <strong>「{{ name }}」</strong>
            <span>{{ directionGroup.titleSuffix }}</span>
          </div>
          <div
            class="relation-item"
            v-for="relationEntitiesItem in directionGroup.groups.slice(
              0,
              ui.defaultRelationNum
            )"
            :key="directionGroup.type + relationEntitiesItem.name"
          >
            <div class="relation-name">
              <span>{{ relationEntitiesItem.name }}</span>
              <em>{{ relationEntitiesItem.relationList.length }}</em>
            </div>
            <el-space wrap class="relation-value-wrapper">
              <template
                v-for="(
                  relationItem, relationIndex
                ) in relationEntitiesItem.relationList.slice(
                  0,
                  relationEntitiesItem.showNum
                )"
                :key="relationIndex"
              >
                <el-dropdown
                  v-if="adminMode && directionGroup.type === 'outgoing'"
                  trigger="contextmenu"
                >
                  <el-tag
                    class="relation-value"
                    :class="{ 'selected-relation': relationItem.selected }"
                    :disable-transitions="true"
                    @click.stop="handleTagClick(relationItem)"
                  >
                    {{ getRelationEntityName(relationItem) }}
                  </el-tag>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item
                        @click="$emit('relation-edit', relationItem)"
                      >
                        编辑
                      </el-dropdown-item>
                      <el-dropdown-item
                        @click="$emit('relation-delete', relationItem)"
                      >
                        删除
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
                <el-tag
                  v-else
                  class="relation-value"
                  :class="{ 'selected-relation': relationItem.selected }"
                  :disable-transitions="true"
                  @click.stop="handleTagClick(relationItem)"
                >
                  {{ getRelationEntityName(relationItem) }}
                </el-tag>
              </template>
              <el-tag
                :disable-transitions="true"
                class="more"
                v-if="handleCollapseMoreItemBtnVisible(relationEntitiesItem)"
                @click.stop="handleSpreadRelationEntity(relationEntitiesItem)"
              >
                +{{
                  relationEntitiesItem.relationList.length -
                  defaultRelationEntityNum
                }}项
              </el-tag>
            </el-space>
          </div>
        </div>
      </div>
      <div class="spread-informatin" v-if="ui.ifRelationCollapseVisible">
        <el-button
          size="small"
          @click="handleSpreadRelation(relationEntitiesList.length)"
        >
          <i
            class="iconfont icon-doublearrow"
            :class="{ 'all-information': !ui.ifRelationCollapse }"
          ></i>
          <span>{{ ui.ifRelationCollapse ? "展开" : "收起" }}</span>
        </el-button>
      </div>
    </div>
  </div>

  <el-skeleton v-show="!ready" :animated="true">
    <template #template>
      <el-skeleton-item class="title" variant="text" /><br />
      <el-skeleton-item class="entity-type-tag" variant="text" /><br />
      <el-skeleton-item class="entity-description-title" variant="text" /><br />
      <el-skeleton-item
        class="description-info"
        variant="text"
        v-for="index in 4"
        :key="index"
      /><br />
      <el-skeleton-item class="entity-attribute-title" variant="text" /><br />
      <div class="attribute-content" v-for="index in 3" :key="index">
        <div class="attribute-item">
          <el-skeleton-item class="attribute-name" variant="text" />
          <el-skeleton-item class="attribute-value" variant="text" />
        </div>
        <div class="attribute-item">
          <el-skeleton-item class="attribute-name" variant="text" />
          <el-skeleton-item class="attribute-value" variant="text" />
        </div>
      </div>
      <el-skeleton-item class="entity-relation-title" variant="text" /><br />
      <div class="all-relation" v-for="index in 2" :key="index">
        <div class="relation-item">
          <el-skeleton-item class="relation-name" variant="text" />
          <div class="relation-value-wrapper">
            <el-skeleton-item
              class="relation-value"
              v-for="idx in 5"
              :key="idx"
              variant="text"
            />
          </div>
        </div>
      </div>
    </template>
  </el-skeleton>
</template>

<script setup>
import { Plus } from "@element-plus/icons-vue";
import { formatAttributeDisplayName } from "@/utils/entityAttribute";
import {
  computed,
  defineEmits,
  defineProps,
  nextTick,
  reactive,
  ref,
  watch,
} from "vue";

const props = defineProps({
  entityId: { type: String, default: "" },
  name: { type: String, default: "" },
  disambiguation: { type: String, default: "" },
  typeList: { type: Array, default: () => [] },
  typeCodes: { type: Array, default: () => [] },
  alias: { type: Array, default: () => [] },
  categoryOptions: { type: Array, default: () => [] },
  description: { type: String, default: "" },
  attributesList: { type: Array, default: () => [] },
  relationEntitiesList: { type: Array, default: () => [] },
  ready: { type: Boolean, default: false },
  adminMode: { type: Boolean, default: false },
  editable: { type: Boolean, default: false },
});

const emit = defineEmits([
  "entity-click",
  "relation-edit",
  "relation-delete",
  "update:description",
  "update:attributesList",
  "update:alias",
  "update:typeCodes",
]);

const defaultAttributeNum = 6;
const defaultRelationNum = 5;
const defaultRelationEntityNum = 5;

const descriptionInfoRef = ref(null);
const aliasInput = ref("");

function normalizeAlias(value) {
  return value == null ? "" : String(value).trim();
}

function addAliasFromInput() {
  const text = normalizeAlias(aliasInput.value);
  if (!text) {
    return;
  }
  const exists = (props.alias || []).some(
    (item) => normalizeAlias(item) === text
  );
  if (!exists) {
    emit("update:alias", [...(props.alias || []), text]);
  }
  aliasInput.value = "";
}

function removeAlias(index) {
  const next = [...(props.alias || [])];
  next.splice(index, 1);
  emit("update:alias", next);
}

const selectedCategoryItems = computed(() => {
  const options = props.categoryOptions || [];
  return (props.typeCodes || []).map((code) => {
    const matched = options.find((item) => item.code === code);
    return matched || { code, name: code };
  });
});

const availableCategoryOptions = computed(() => {
  const selected = new Set(props.typeCodes || []);
  return (props.categoryOptions || []).filter(
    (item) => !selected.has(item.code)
  );
});

function addTypeCode(code) {
  if (!code || (props.typeCodes || []).includes(code)) {
    return;
  }
  emit("update:typeCodes", [...(props.typeCodes || []), code]);
}

function removeTypeCode(code) {
  const current = [...(props.typeCodes || [])];
  const idx = current.indexOf(code);
  if (idx === -1) {
    return;
  }
  current.splice(idx, 1);
  emit("update:typeCodes", current);
}

const ui = reactive({
  defaultAttributeNum,
  defaultRelationNum,
  ifDescriptionCollapseVisible: false,
  ifAttributionCollapseVisible: false,
  ifRelationCollapseVisible: false,
  ifDescriptionCollapse: false,
  ifAttributionCollapse: false,
  ifRelationCollapse: false,
});

const relationDirectionGroups = computed(() => {
  const incomingGroups = [];
  const outgoingGroups = [];

  (props.relationEntitiesList || []).forEach((relationGroup) => {
    const incomingList = relationGroup.relationList.filter(
      (relationItem) => relationItem.targetId === props.entityId
    );
    const outgoingList = relationGroup.relationList.filter(
      (relationItem) => relationItem.sourceId === props.entityId
    );

    if (incomingList.length) {
      incomingGroups.push({
        ...relationGroup,
        relationList: incomingList,
        sourceGroup: relationGroup,
        showNum: relationGroup.incomingShowNum,
        showNumKey: "incomingShowNum",
      });
    }

    if (outgoingList.length) {
      outgoingGroups.push({
        ...relationGroup,
        relationList: outgoingList,
        sourceGroup: relationGroup,
        showNum: relationGroup.outgoingShowNum,
        showNumKey: "outgoingShowNum",
      });
    }
  });

  return [
    {
      type: "incoming",
      icon: "《",
      titlePrefix: "以下是",
      titleSuffix: "的：",
      groups: incomingGroups,
    },
    {
      type: "outgoing",
      icon: "》",
      titlePrefix: "",
      titleSuffix: "是以下的：",
      groups: outgoingGroups,
    },
  ].filter((directionGroup) => directionGroup.groups.length);
});

const showRelationSection = computed(
  () =>
    !props.editable &&
    (relationDirectionGroups.value.length > 0 || props.adminMode)
);

function getRelationEntityId(relationItem) {
  return relationItem.targetId === props.entityId
    ? relationItem.sourceId
    : relationItem.targetId;
}

function getRelationEntityName(relationItem) {
  return relationItem.targetId === props.entityId
    ? relationItem.sourceName
    : relationItem.targetName;
}

function updateAttributeField(index, field, value) {
  const next = (props.attributesList || []).map((item, i) =>
    i === index ? { ...item, [field]: value } : item
  );
  emit("update:attributesList", next);
}

function addEditableAttribute() {
  emit("update:attributesList", [
    ...(props.attributesList || []),
    { name: "", value: "" },
  ]);
}

function removeEditableAttribute(index) {
  const next = [...(props.attributesList || [])];
  next.splice(index, 1);
  emit("update:attributesList", next);
}

function handleTagClick(relationItem) {
  emit("entity-click", {
    relationItem,
    entityId: getRelationEntityId(relationItem),
  });
}

function handleCollapseDescriptionBtnVisible() {
  const textEl = descriptionInfoRef.value?.querySelector(".description-text");
  const infoEl = descriptionInfoRef.value;
  if (!textEl || !infoEl) {
    ui.ifDescriptionCollapseVisible = false;
    return;
  }
  if (textEl.offsetHeight < infoEl.offsetHeight - 30) {
    ui.ifDescriptionCollapseVisible = false;
  } else {
    ui.ifDescriptionCollapseVisible = true;
    ui.ifDescriptionCollapse = true;
  }
}

function handleSpreadDescription() {
  const infoEl = descriptionInfoRef.value;
  const justifyEl = infoEl?.querySelector(".kn-text-align-justify");
  const textEl = infoEl?.querySelector(".description-text");
  if (!infoEl || !textEl) {
    return;
  }
  if (ui.ifDescriptionCollapse) {
    infoEl.style.maxHeight = textEl.offsetHeight + 30 + "px";
    justifyEl?.classList.remove("kn-ellipsis-multiple");
    ui.ifDescriptionCollapse = false;
  } else {
    infoEl.style.maxHeight = "110px";
    justifyEl?.classList.add("kn-ellipsis-multiple");
    ui.ifDescriptionCollapse = true;
  }
}

function handleCollapseAttributionBtnVisible() {
  ui.ifAttributionCollapseVisible =
    (props.attributesList?.length || 0) > ui.defaultAttributeNum;
  if (ui.ifAttributionCollapseVisible) {
    ui.ifAttributionCollapse = true;
  }
}

function handleSpreadAttribute(attributesListLength) {
  if (ui.ifAttributionCollapse) {
    ui.defaultAttributeNum = attributesListLength;
    ui.ifAttributionCollapse = false;
  } else {
    ui.defaultAttributeNum = defaultAttributeNum;
    ui.ifAttributionCollapse = true;
  }
}

function handleCollapseRelationBtnVisible() {
  ui.ifRelationCollapseVisible =
    (props.relationEntitiesList?.length || 0) > ui.defaultRelationNum;
  if (ui.ifRelationCollapseVisible) {
    ui.ifRelationCollapse = true;
  }
}

function handleSpreadRelation(relationEntitiesListLength) {
  if (ui.ifRelationCollapse) {
    ui.defaultRelationNum = relationEntitiesListLength;
    ui.ifRelationCollapse = false;
  } else {
    ui.defaultRelationNum = defaultRelationNum;
    ui.ifRelationCollapse = true;
  }
}

function handleCollapseMoreItemBtnVisible(relationEntitiesItem) {
  return (
    relationEntitiesItem.relationList.length > relationEntitiesItem.showNum
  );
}

function handleSpreadRelationEntity(relationEntitiesItem) {
  if (relationEntitiesItem.sourceGroup && relationEntitiesItem.showNumKey) {
    relationEntitiesItem.sourceGroup[relationEntitiesItem.showNumKey] =
      relationEntitiesItem.relationList.length;
    return;
  }
  relationEntitiesItem.showNum = relationEntitiesItem.relationList.length;
}

function resetCollapseUi() {
  ui.defaultAttributeNum = defaultAttributeNum;
  ui.defaultRelationNum = defaultRelationNum;
  ui.ifDescriptionCollapseVisible = false;
  ui.ifAttributionCollapseVisible = false;
  ui.ifRelationCollapseVisible = false;
  ui.ifDescriptionCollapse = false;
  ui.ifAttributionCollapse = false;
  ui.ifRelationCollapse = false;
}

watch(
  () => props.ready,
  (isReady) => {
    if (isReady) {
      nextTick(() => {
        handleCollapseDescriptionBtnVisible();
        handleCollapseAttributionBtnVisible();
        handleCollapseRelationBtnVisible();
      });
    } else {
      resetCollapseUi();
    }
  }
);

watch(
  () => props.entityId,
  () => {
    aliasInput.value = "";
    resetCollapseUi();
  }
);

watch(
  () => props.editable,
  (isEditable) => {
    if (!isEditable) {
      aliasInput.value = "";
    }
  }
);
</script>

<style lang="scss" scoped>
.entity-detail {
  padding: 0 50px;
  text-align: left;

  .entity-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .title {
    flex: 1;
    min-width: 0;

    h1 {
      display: inline-block;
      font-size: 36px;
      font-weight: bold;
    }
    span {
      padding-left: 10px;
      font-size: 16px;
    }
  }

  .entity-basic-info-actions {
    flex-shrink: 0;
  }

  .entity-meta {
    margin-top: 12px;

    .meta-field {
      display: block;
      width: 100%;
      margin-bottom: 4px;

      .meta-label {
        display: block;
        font-weight: bold;
        font-size: 16px;
        color: #101010;
        margin: 20px 0 10px 0;
        line-height: 1.4;
      }

      .meta-body {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
        width: 100%;
      }

      .meta-empty-hint {
        color: #909399;
        font-size: 14px;
        line-height: 28px;
      }

      .alias-add-input {
        width: 200px;

        :deep(.el-input__wrapper) {
          border-radius: 16px;
          box-shadow: 0 0 0 1px #dcdfe6 inset;
        }
      }

      .entity-alias-tag {
        height: 28px;
        padding: 0 14px;
        border: none;
        background-color: #eef4fc;
        color: #2f5f8f;
        border-radius: 14px;
        line-height: 28px;
        font-size: 14px;
      }

      &.meta-field--type {
        .meta-add-btn {
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          padding: 0;
        }
      }

      .el-tag.entity-type-tag {
        height: 30px;
        margin: 0;
        padding: 0 20px;
        border: none;
        background-color: #1a73e8;
        color: #fff;
        border-radius: 15px;
        line-height: 30px;
        font-size: 16px;

        &.entity-type-tag--editable {
          :deep(.el-tag__close) {
            color: rgba(255, 255, 255, 0.85);

            &:hover {
              color: #fff;
              background-color: rgba(255, 255, 255, 0.2);
            }
          }
        }
      }
    }
  }

  .entity-description,
  .entity-attribute,
  .entity-relation {
    color: #666;

    h3 {
      font-weight: bold;
      font-size: 16px;
      color: #101010;
      margin: 50px 0 10px 0;
    }

    .spread-informatin {
      height: 35px;
      text-align: center;

      .el-button {
        margin: 0 auto;
        margin-top: 10px;
        line-height: 35px;
        cursor: pointer;
        color: #3894ff;
        border: none;
        --el-button-hover-bg-color: transparent;

        .iconfont {
          margin-right: 5px;
          vertical-align: middle;
          font-size: 12px;
        }

        .all-information {
          display: inline-block;
          transform: rotate(180deg) translateY(2px);
        }
      }
    }
  }

  .entity-relation-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 50px 0 10px 0;

    .entity-relation-title {
      margin: 0;
    }
  }

  .entity-description {
    .description-info {
      padding: 15px 14px;
      max-height: 110px;
      background-color: #f9f9f9;
      line-height: 20px;
    }
  }

  .entity-attribute {
    .attr-edit-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .attribute-content {
      padding: 20px 14px 0 14px;
      background-color: #f9f9f9;

      .el-col {
        line-height: 20px;
        padding-bottom: 20px;
        padding-right: 20px;

        .attribute-item {
          display: flex;

          .attribute-name {
            width: 90px;
            color: #101010;
            font-weight: bold;
            padding-right: 10px;
          }

          .attribute-value {
            flex: 1;
            color: #101010;
          }
        }
      }
    }
  }

  .entity-relation {
    padding-bottom: 32px;

    .relation-empty-hint {
      margin: 0 0 8px;
      color: #909399;
      font-size: 14px;
    }

    .all-relation {
      display: flex;
      flex-direction: column;
      gap: 14px;

      .relation-direction-card {
        padding: 16px 18px 14px 18px;
        border-radius: 8px;
        border-left: 3px solid #64a6e1;
        background-color: #f3f8ff;

        &.outgoing {
          border-left-color: #e6a23c;
          background-color: #fff8ec;

          .direction-icon,
          .relation-direction-title strong {
            color: #b97714;
          }

          .relation-name em {
            background-color: #f6e3c0;
            color: #b97714;
          }
        }

        .relation-direction-title {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 14px;
          color: #4f5f73;
          font-size: 13px;
          font-weight: 600;

          .direction-icon {
            margin-right: 4px;
            color: #2f7ecb;
            font-size: 18px;
            line-height: 1;
          }

          strong {
            color: #1a73e8;
          }
        }

        .relation-item {
          display: flex;
          align-items: flex-start;
          padding: 11px 0;
          border-top: 1px dashed rgba(100, 166, 225, 0.24);

          &:first-of-type {
            border-top: none;
          }

          .relation-name {
            display: flex;
            align-items: center;
            flex-shrink: 0;
            width: 90px;
            min-height: 26px;
            line-height: 26px;
            color: #101010;
            font-weight: bold;

            em {
              height: 18px;
              min-width: 18px;
              margin-left: 6px;
              padding: 0 5px;
              border-radius: 9px;
              background-color: #dcecff;
              color: #2f7ecb;
              font-size: 12px;
              line-height: 18px;
              text-align: center;
              font-style: normal;
              font-weight: 500;
            }
          }

          .el-space {
            flex: 1;
            align-items: center;

            :deep(.el-space__item) {
              margin-right: 10px !important;
              margin-bottom: 7px;
            }

            .el-tag.relation-value {
              height: 26px;
              border: 1px solid #e5e8ef;
              border-radius: 13px;
              padding: 0 15px;
              cursor: pointer;
              line-height: 24px;
              font-size: 14px;
              background-color: #fff;
              color: #101010;
              box-shadow: 0 1px 3px rgba(16, 16, 16, 0.05);

              &:hover {
                color: #64a6e1;
                border-color: #9ac8f0;
              }

              &.selected-relation {
                color: #fff;
                border-color: #64a6e1;
                background-color: #64a6e1;
                &:hover {
                  color: #fff;
                }
              }

              &.more {
                color: #64a6e1;
                background-color: #fff;
                border: #64a6e1 1px solid;
              }
            }
          }
        }
      }
    }
  }
}

.el-skeleton {
  width: 100%;
  padding: 0 50px;
  text-align: left;

  .title {
    width: 200px !important;
    height: 45px;
  }

  .entity-type-tag {
    width: 100px;
    height: 30px;
    margin-top: 10px;
    border-radius: 15px;
  }

  .entity-description-title,
  .entity-attribute-title,
  .entity-relation-title {
    width: 40px;
    height: 20px;
    margin: 50px 0 10px 0;
  }

  .description-info {
    height: 20px;
    margin-bottom: 3px;
  }

  .attribute-content {
    display: flex;
    margin-bottom: 7px;
    .attribute-item {
      flex: 1;
      display: flex;
      .attribute-name {
        width: 70px;
        height: 20px;
        margin-right: 20px;
      }
      .attribute-value {
        width: 300px;
        height: 20px;
      }
    }
  }

  .all-relation {
    display: flex;
    margin-bottom: 7px;
    .relation-item {
      flex: 1;
      display: flex;
      .relation-name {
        width: 70px;
        height: 20px;
        margin-right: 20px;
      }
      .relation-value-wrapper {
        flex: 1;
        .relation-value {
          width: 100px;
          height: 20px;
          border-radius: 10px;
          margin-right: 35px;
        }
      }
    }
  }
}
</style>
